export interface PayrollRunSummary {
  id: string;
  status: string;
  checkDate: string;
  periodStartDate: string;
  periodEndDate: string;
}

export interface PayrollRegisterLine {
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  payrollCalculationId: string | null;
  grossEarnings: number;
  employeeTaxes: number;
  employerTaxes: number;
  preTaxDeductionsTotal: number;
  postTaxDeductionsTotal: number;
  garnishmentsTotal: number;
  netPay: number;
}

export interface PayrollRegisterTotals {
  grossEarnings: number;
  employeeTaxes: number;
  employerTaxes: number;
  preTaxDeductionsTotal: number;
  postTaxDeductionsTotal: number;
  garnishmentsTotal: number;
  netPay: number;
}

export interface PayrollRegisterResponse {
  payrollRunId: string;
  periodStartDate: string;
  periodEndDate: string;
  checkDate: string;
  status: string;
  totals: PayrollRegisterTotals;
  lines: PayrollRegisterLine[];
}

export interface CashRequirementResponse {
  payrollRunId: string;
  checkDate: string;
  status: string;
  totalGrossEarnings: number;
  totalEmployerTaxes: number;
  totalCashRequired: number;
}

export interface EmployeePaySummaryLine {
  payrollRunId: string;
  checkDate: string;
  grossEarnings: number;
  employeeTaxes: number;
  employerTaxes: number;
  preTaxDeductionsTotal: number;
  postTaxDeductionsTotal: number;
  garnishmentsTotal: number;
  netPay: number;
}

export interface EmployeePaySummaryTotals {
  grossEarnings: number;
  employeeTaxes: number;
  employerTaxes: number;
  preTaxDeductionsTotal: number;
  postTaxDeductionsTotal: number;
  garnishmentsTotal: number;
  netPay: number;
}

/** Numeric wire values — mirrors EPN.Employees.Api's own `EmployeeDocumentType` enum exactly (W4=0, I9=1,
 * Other=2, Paystub=3). Paystub (EPN-216 Step 3, product-owner approved) is a REFERENCE-ONLY type: a stored file that never feeds earnings,
 * withholding, net pay or balances. Other formal types (e.g. DE34) still need their own explicit approval. Documents are read and written through
 * `employeeDocumentsGateway` (features/employee-documents). */
export const EmployeeDocumentType = { W4: 0, I9: 1, Other: 2, Paystub: 3 } as const;
export const employeeDocumentTypeLabels: Record<number, string> = { 0: 'W-4', 1: 'I-9', 2: 'Other', 3: 'Paystub' };

/** Who put the file on the record (decided by the server). Mirrors `DocumentSource`. */
export const DocumentSource = { Admin: 0, Employee: 1 } as const;
/** Mirrors `DocumentReviewStatus`. Only what can really be set exists: nothing is ever "Verified" yet. */
export const DocumentReviewStatus = { PendingReview: 0, NeedsCorrection: 1 } as const;
/** Mirrors `CorrectionNotificationState`. */
export const CorrectionNotificationState = { NotSent: 0, Sent: 1 } as const;

export interface EmployeeDocumentResponse {
  id: string;
  employeeId: string;
  documentType: number;
  /** Required when documentType is Other — the approved "Custom Document Type" behavior. Selecting
   * Other must never display/persist the bare word "Other" as the only meaningful classification. */
  customTypeDescription: string | null;
  /** Required for new uploads (enforced server-side), but nullable on the wire — historical rows from
   * before this field existed have no value and are never backfilled with a fabricated one. */
  documentName: string | null;
  expiryDate: string | null;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: string;
  uploadedAtUtc: string;
  source: number;
  reviewStatus: number;
  /** Only set while a correction is open. */
  correctionReason: string | null;
  correctionRequestedAtUtc: string | null;
  correctionNotification: number | null;
}

/** The label to actually show for a document's type.
 *
 * - Not Other: the normal formal type label (W-4/I-9).
 * - Other with a real (non-blank) custom type: that custom type — never the bare word "Other" for a
 *   document that actually has a meaningful classification.
 * - Other with no custom type: falls back to "Other". This is the one legitimate case — a historical row
 *   uploaded before `customTypeDescription` existed as a field, which can never be backfilled with a real
 *   value. New uploads can't reach this branch: `DocumentType = Other` without a custom type is rejected
 *   server-side (see EmployeeDocumentService.UploadAsync), so this fallback is read-only degradation for
 *   old data, not a second way to skip the validation rule. */
export function documentTypeDisplayLabel(doc: Pick<EmployeeDocumentResponse, 'documentType' | 'customTypeDescription'>): string {
  if (doc.documentType === EmployeeDocumentType.Other) {
    const trimmed = doc.customTypeDescription?.trim();
    return trimmed || 'Other';
  }
  return employeeDocumentTypeLabels[doc.documentType] ?? 'Unknown';
}

/** The name to actually show for a document: the trimmed, admin-entered Document Name when present,
 * otherwise the uploaded file's own name. Historical rows uploaded before `documentName` existed as a
 * field have no value to show — falling back to the file name (never blank, "undefined", or "null") is
 * read-only display degradation, not a value the database is backfilled with. */
export function documentDisplayName(doc: Pick<EmployeeDocumentResponse, 'documentName' | 'originalFileName'>): string {
  const trimmed = doc.documentName?.trim();
  return trimmed ? trimmed : doc.originalFileName;
}

export interface EmployeePaySummaryResponse {
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  from: string;
  to: string;
  totals: EmployeePaySummaryTotals;
  lines: EmployeePaySummaryLine[];
}

export interface PayslipTaxLine {
  description: string;
  amount: number;
}

export interface PayslipLeaveLine {
  leavePlanName: string;
  used: number;
  accrued: number;
  available: number;
}

export interface PayslipYtdTotals {
  grossEarnings: number;
  employeeTaxes: number;
  employerTaxes: number;
  preTaxDeductionsTotal: number;
  postTaxDeductionsTotal: number;
  garnishmentsTotal: number;
  netPay: number;
}

/** EPN-73. Deliberately missing fields the approved design shows but no backend source exists for yet:
 * per-earning-line breakdown (only the aggregate `grossEarnings` is available), an employer-benefit-
 * contribution breakdown and employee phone/address. See `docs/epn-73-payslip-gaps.md`. */
export interface EmployeePayslipResponse {
  payrollRunId: string;
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  ssnLast4: string | null;
  companyName: string | null;
  periodStartDate: string;
  periodEndDate: string;
  checkDate: string;
  /** Wire value, derived server-side (EPN-237) from existing facts: Approved, Processing, Paid or Voided. Parse with
   * `payslipStatusView` (payslipStatus.ts), which degrades any unrecognised value safely instead of trusting it. */
  status: string;
  /** EPN-237 / EPN-239: the UTC INSTANT of the event that set the status (approval, submission, settlement or reversal), ISO-8601 with a trailing Z, when known.
   * Shown with its time in the viewer's chosen time zone (timeZone.ts). Never use it as a pay date: `checkDate` stays a plain calendar date. */
  statusSince?: string | null;
  /** EPN-237: `DirectDeposit`, `Check` or null (not known yet; shown as "---"). */
  payMethod?: string | null;
  /** EPN-237: the Paid / Processing fact came from a mock provider; Paid must then read "Paid (simulated)". */
  simulated?: boolean;
  /** EPN-237: a payment batch failed. The payslip stays Approved and shows a "Payment failed" notice. */
  paymentFailed?: boolean;
  /** EPN-237: false when the caller may not read payment facts; every payslip then shows as Approved. Absent = true. */
  paymentStatusAvailable?: boolean;
  /** EPN-237: false when the caller may not read reversals, so Voided cannot be verified. Absent = true. */
  reversalsAvailable?: boolean;
  /** UI-only, optional: payment memo. NO backend source exists; shown only when present (Mock fixtures only). */
  memo?: string | null;
  grossEarnings: number;
  employeeTaxLines: PayslipTaxLine[];
  employerTaxLines: PayslipTaxLine[];
  employeeTaxesTotal: number;
  employerTaxesTotal: number;
  preTaxDeductionsTotal: number;
  postTaxDeductionsTotal: number;
  garnishmentsTotal: number;
  netPay: number;
  leave: PayslipLeaveLine[];
  ytd: PayslipYtdTotals;
}

/** EPN-237: one row of `GET /api/reports/employee-payslips` — one employee on one Approved payroll run. Dates are
 * `YYYY-MM-DD`; `statusSince` is a UTC instant (ISO-8601 with a trailing Z). `grossPay` is null only if the calculation snapshot could not be read. */
export interface EmployeePayslipListItem {
  payrollRunId: string;
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  periodStartDate: string;
  periodEndDate: string;
  checkDate: string;
  grossPay: number | null;
  netPay: number;
  /** Wire value; parse with `payslipStatusView`. */
  status: string;
  statusSince: string | null;
  /** `DirectDeposit`, `Check` or null. */
  payMethod: string | null;
  simulated: boolean;
  paymentFailed: boolean;
}

export interface EmployeePayslipListPeriod {
  start: string;
  end: string;
  checkDate: string;
}

/** Company-wide figures over every Approved run considered; the list's own filters do not narrow them. */
export interface EmployeePayslipListSummary {
  totalNetPayYtd: number;
  ytdYear: number;
  employeesWithPayslips: number;
  lastPeriod: EmployeePayslipListPeriod | null;
  approvedCount: number;
}

export interface EmployeePayslipPayPeriod {
  payrollRunId: string;
  periodStartDate: string;
  periodEndDate: string;
  checkDate: string;
}

export interface EmployeePayslipListResponse {
  items: EmployeePayslipListItem[];
  page: number;
  pageSize: number;
  total: number;
  summary: EmployeePayslipListSummary;
  /** Every Approved run considered, newest pay date first: the options of the Pay Period filter. */
  payPeriods: EmployeePayslipPayPeriod[];
  /** False: the caller lacks the permission to read payment facts, so every payslip is shown as Approved. */
  paymentStatusAvailable: boolean;
  /** False: the caller lacks the permission to read reversals, so Voided cannot be verified. */
  reversalsAvailable: boolean;
  /** True when the company has more Approved runs than one request considers (the most recent are included). */
  runsTruncated: boolean;
}

/** Server-side filters and paging of the Employee Payslips list. */
export interface EmployeePayslipListQuery {
  page: number;
  pageSize: number;
  payDateFrom?: string;
  payDateTo?: string;
  status?: string;
  payMethod?: string;
  search?: string;
  payrollRunId?: string;
  employeeId?: string;
  /** EPN-239: `checkDate:desc` (default), `checkDate:asc`, `employeeName:asc` or `employeeName:desc`. */
  sort?: string;
}
